import { useState, useEffect, useRef } from "react";
import { Bot, Send, Trash2, Loader2, User, Cpu } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import axios from "axios";

export default function SmartPayAssistant() {
  const { toast } = useToast();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { loadHistory(); }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const loadHistory = async () => {
    try { const res = await axios.get("/api/smartpay/chat/history"); setMessages(res.data.messages || []); } catch {}
  };

  const handleSend = async () => {
    if (!input.trim() || sending) return;
    const userMsg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMsg }]);
    setSending(true);
    try {
      const res = await axios.post("/api/smartpay/chat", { message: userMsg });
      setMessages(prev => [...prev, { role: "assistant", content: res.data.response }]);
    } catch {
      toast({ title: "Chat failed", variant: "destructive" });
      setMessages(prev => prev.slice(0, -1));
    } finally { setSending(false); }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleClear = async () => {
    try { await axios.delete("/api/smartpay/chat/history"); setMessages([]); toast({ title: "Chat history cleared" }); } catch { toast({ title: "Failed to clear", variant: "destructive" }); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">SmartPay AI Assistant</h1>
          <p className="text-muted-foreground">Your AllBaise Resource Management copilot — contractors, HRIS, equipment & more</p>
        </div>
        {messages.length > 0 && <Button variant="outline" size="sm" onClick={handleClear}><Trash2 className="h-4 w-4 mr-1" /> Clear</Button>}
      </div>

      <Card className="flex flex-col h-[60vh]">
        <CardHeader className="border-b py-3">
          <CardTitle className="flex items-center gap-2 text-sm"><Bot className="h-5 w-5 text-teal-500" /> SmartPay Assistant (Resource Management Copilot)</CardTitle>
        </CardHeader>
        <CardContent className="flex-1 p-0">
          <ScrollArea ref={scrollRef} className="h-full p-4">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground space-y-3">
                <Bot className="h-12 w-12 text-teal-300" />
                <p className="text-lg font-medium">How can I help you with Resource Management?</p>
                <p className="text-sm max-w-md">Ask about contractors, HR operations, equipment status, or get AI-powered recommendations.</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {["Show active contractors", "Who's onboarding?", "Device health overview", "Create an employee record"].map((q, i) => (
                    <Button key={i} variant="outline" size="sm" className="text-xs" onClick={() => setInput(q)}>{q}</Button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((msg, i) => (
                  <div key={i} className={"flex gap-3 " + (msg.role === "user" ? "justify-end" : "")}>
                    {msg.role === "assistant" && <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0"><Cpu className="h-4 w-4 text-teal-600" /></div>}
                    <div className={"max-w-[80%] rounded-lg p-3 " + (msg.role === "user" ? "bg-teal-500 text-white" : "bg-slate-100 text-slate-800")}>
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                    {msg.role === "user" && <div className="w-8 h-8 rounded-full bg-teal-500 flex items-center justify-center flex-shrink-0"><User className="h-4 w-4 text-white" /></div>}
                  </div>
                ))}
                {sending && (
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center"><Loader2 className="h-4 w-4 text-teal-600 animate-spin" /></div>
                    <div className="bg-slate-100 rounded-lg p-3">
                      <div className="flex gap-1"><div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} /><div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} /><div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} /></div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
        </CardContent>
        <div className="border-t p-3">
          <div className="flex gap-2">
            <Input value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown} placeholder="Ask about resource management..." disabled={sending} className="flex-1" />
            <Button onClick={handleSend} disabled={!input.trim() || sending}><Send className="h-4 w-4" /></Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
